import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Empty, Input, Spin, Tag, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  type CustomElement,
  type CustomElementType,
  customElementsApi,
} from '@/api/customElements';
import { useCurrentUserEmail } from '@/auth/useCurrentUserEmail';
import { ConfirmPopover } from '@/components/ConfirmPopover';
import { PageHeader } from '@/components/PageHeader';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

const { Text } = Typography;

const KIND_COLOR: Record<CustomElementType, string> = {
  Field: 'blue',
  Section: 'purple',
  Step: 'orange',
};

/**
 * Library of every saved custom field/section/step — the originator's own elements
 * first (then a divider, then everyone else's), with search. Author-only Delete;
 * everyone can navigate in to view, and only the author can save changes once there
 * (the editor page re-checks authorship server-side on every Save).
 */
export function CustomElementSelectorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const myEmail = useCurrentUserEmail();

  const [elements, setElements] = useState<CustomElement[] | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const list = await customElementsApi.list();
      setElements(list);
    } catch (err) {
      handleApiError(err, t, t('workflow.customElements.editLoadFailed'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (elements ?? []).filter(
      (el) =>
        needle === '' ||
        el.nameEn.toLowerCase().includes(needle) ||
        el.nameFr.toLowerCase().includes(needle),
    );
  }, [elements, search]);

  const mine = filtered.filter((el) => myEmail !== null && el.author === myEmail);
  const others = filtered.filter((el) => !(myEmail !== null && el.author === myEmail));

  const handleDelete = async (el: CustomElement) => {
    try {
      await customElementsApi.remove(el.id);
      message.success(t('workflow.customElements.deleted'));
      await load();
    } catch (err) {
      handleApiError(err, t, t('workflow.customElements.deleteFailed'));
    }
  };

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
      <PageHeader
        title={t('workflow.customElements.selectorTitle')}
        onBack={() => navigate('/originator/dashboard')}
      />
      <Text style={{ display: 'block', margin: '8px 0 20px', color: '#475467' }}>
        {t('workflow.customElements.selectorSubtitle')}
      </Text>

      <Input
        placeholder={t('workflow.customElements.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        suffix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
        style={{ marginBottom: 16, maxWidth: 360 }}
      />

      {elements === null ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : filtered.length === 0 ? (
        <Empty description={t('workflow.customElements.noneFound')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {mine.length > 0 && (
            <ElementGroup
              headerKey="workflow.customElements.mineHeader"
              items={mine}
              myEmail={myEmail}
              onDelete={handleDelete}
            />
          )}
          {mine.length > 0 && others.length > 0 && (
            <div style={{ borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />
          )}
          {others.length > 0 && (
            <ElementGroup
              headerKey="workflow.customElements.othersHeader"
              items={others}
              myEmail={myEmail}
              onDelete={handleDelete}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ElementGroup({
  headerKey,
  items,
  myEmail,
  onDelete,
}: {
  headerKey: string;
  items: CustomElement[];
  myEmail: string | null;
  onDelete: (el: CustomElement) => Promise<void>;
}) {
  const { t } = useTranslation();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Text
        style={{
          fontSize: 11,
          fontWeight: 700,
          // #6b7280 reaches only 4.46:1 against the #f5f6f7 panel — a hair
          // under the 4.5:1 this 11px text needs (WCAG 1.4.3). It passes on
          // pure white, which is why it reads as fine elsewhere.
          color: '#5b6472',
          letterSpacing: 0.6,
          textTransform: 'uppercase',
        }}
      >
        {t(headerKey)}
      </Text>
      {items.map((el) => (
        <ElementCard
          key={el.id}
          el={el}
          canDelete={myEmail !== null && el.author === myEmail}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function ElementCard({
  el,
  canDelete,
  onDelete,
}: {
  el: CustomElement;
  canDelete: boolean;
  onDelete: (el: CustomElement) => Promise<void>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    // The ROW is not a control. It used to be `<div role="button" tabIndex={0}>`
    // wrapping the delete Button, which nests one interactive control inside
    // another (WCAG 4.1.2) — screen readers announce a button containing a
    // button, and the inner one is unreachable by the outer's key handling.
    // Instead the row is a plain flex container holding two siblings: a real
    // <button> covering the row's content for the primary navigate action, and
    // the delete button. Native <button> brings Enter/Space for free, so the
    // hand-rolled onKeyDown, the click-swallowing wrapper span, and two eslint
    // suppressions all go away.
    //
    // Layout (and the hover lift) live in global.css under `.ce-row` so the
    // phone band can drop the author + date columns and let the name wrap —
    // an inline width here would outrank that band.
    <div className="ce-row">
      <button
        type="button"
        className="ce-row__main"
        onClick={() => navigate(`/originator/custom-elements/${el.id}/edit`)}
        aria-label={t('workflow.list.actionAria', {
          action: t('workflow.list.edit'),
          name: el.nameEn,
        })}
      >
        <Tag color={KIND_COLOR[el.type]} style={{ margin: 0, flexShrink: 0 }}>
          {t(`workflow.customElements.kind${el.type}`)}
        </Tag>
        {/* Truncation / wrapping is `.ce-row__name`'s, not AntD's `ellipsis`
            prop — see the rule for why the prop can't be used here. */}
        <Text strong className="ce-row__name" style={{ fontSize: 13, color: '#111827' }}>
          {el.nameEn}
        </Text>
        <Text className="ce-row__author" style={{ fontSize: 12, color: '#6b7280' }}>
          {el.author}
        </Text>
        <Text className="ce-row__date" style={{ fontSize: 12, color: '#6b7280' }}>
          {new Date(el.updatedAt).toLocaleDateString()}
        </Text>
      </button>
      <ConfirmPopover
        title={t('workflow.customElements.deleteConfirmTitle')}
        description={t('workflow.customElements.deleteConfirmDesc')}
        onConfirm={() => onDelete(el)}
        disabled={!canDelete}
      >
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          disabled={!canDelete}
          aria-label={t('workflow.customElements.deleteConfirmTitle')}
        />
      </ConfirmPopover>
    </div>
  );
}
